import { useState } from 'react';
import { copyText } from '@/lib/clipboard';
import { formatCount, formatExact, formatPercent } from '@/lib/format';
import { predicateForBin, predicateForValue, type ColumnProfile } from '@/cache/stats';
import type { ColumnSchema } from '@/types/api';

export interface ColumnDetailProps {
  readonly column: ColumnSchema;
  readonly profile: ColumnProfile;
  readonly onBack: () => void;
  /** Appends a predicate to the active cell's SQL. */
  readonly onUsePredicate: (predicate: string) => void;
}

export function ColumnDetail({
  column,
  profile,
  onBack,
  onUsePredicate,
}: ColumnDetailProps): JSX.Element {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (text: string): Promise<void> => {
    const ok = await copyText(text);
    setCopied(ok ? 'Copied' : 'Copy blocked');
    window.setTimeout(() => setCopied(null), 1400);
  };

  const peakBin = profile.bins.reduce((max, bin) => Math.max(max, bin.count), 0);
  const peakTop = profile.top.reduce((max, entry) => Math.max(max, entry.count), 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="column-detail">
      <div className="panel-head">
        <button type="button" className="icon-btn" onClick={onBack} aria-label="Back to all columns">
          ←
        </button>
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-ink">{column.name}</span>
        <span className="eyebrow shrink-0">{column.typeText}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <dl className="grid grid-cols-2 gap-x-3 border-b border-line px-2.5 py-2 text-xs">
          <Stat label="Rows" value={formatExact(profile.total)} />
          <Stat label="Distinct" value={formatExact(profile.distinct)} />
          <Stat
            label="NULL"
            value={`${formatExact(profile.nullCount)} · ${formatPercent(profile.nullFraction)}`}
            emphasis={profile.nullCount > 0}
          />
          <Stat label="Non-null" value={formatExact(profile.nonNull)} />
          {profile.mean !== null ? (
            <Stat label="Mean" value={profile.mean.toFixed(4)} />
          ) : null}
          <Stat label="Min" value={profile.min ?? 'NULL'} mono copyable onCopy={copy} />
          <Stat label="Max" value={profile.max ?? 'NULL'} mono copyable onCopy={copy} />
        </dl>

        {profile.bins.length > 0 && peakBin > 0 ? (
          <section className="border-b border-line px-2.5 py-2">
            <h3 className="eyebrow mb-1.5">
              {profile.kind === 'timeseries' ? 'Over time' : 'Distribution'}
            </h3>
            <div className="space-y-px">
              {profile.bins.map((bin) => (
                <button
                  key={bin.index}
                  type="button"
                  className="group flex w-full items-center gap-2 rounded-sm px-1 py-0.5 text-left transition-colors hover:bg-raised"
                  title={`Filter to ${bin.label}`}
                  onClick={() => onUsePredicate(predicateForBin(column.name, bin, profile.kind))}
                >
                  <span className="tnum w-[104px] shrink-0 truncate text-[10px] text-faint">
                    {bin.label}
                  </span>
                  <span className="h-2.5 flex-1 bg-line/50">
                    <span
                      className="block h-full bg-local"
                      style={{ width: `${(bin.count / peakBin) * 100}%`, opacity: 0.85 }}
                    />
                  </span>
                  <span className="tnum w-11 shrink-0 text-right text-[10px] text-muted">
                    {formatCount(bin.count)}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {profile.top.length > 0 ? (
          <section className="px-2.5 py-2">
            <h3 className="eyebrow mb-1.5">
              Top values{profile.distinct > profile.top.length ? ` of ${formatCount(profile.distinct)}` : ''}
            </h3>
            <div className="space-y-px">
              {profile.top.map((entry, index) => (
                <button
                  key={`${entry.value ?? 'null'}-${index}`}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-1 py-0.5 text-left transition-colors hover:bg-raised"
                  title={`Filter to ${entry.isNull ? 'NULL' : entry.value}`}
                  onClick={() => onUsePredicate(predicateForValue(column.name, entry))}
                >
                  <span
                    className={`w-[104px] shrink-0 truncate font-mono text-[11px] ${
                      entry.isNull ? 'v-null' : entry.isEmpty ? 'v-empty' : 'text-ink'
                    }`}
                  >
                    {entry.isNull ? 'NULL' : entry.isEmpty ? '""' : entry.value}
                  </span>
                  <span className="h-2.5 flex-1 bg-line/50">
                    <span
                      className="block h-full"
                      style={{
                        width: `${peakTop > 0 ? (entry.count / peakTop) * 100 : 0}%`,
                        background: entry.isNull ? 'var(--c-faint)' : 'var(--c-local)',
                        opacity: 0.85,
                      }}
                    />
                  </span>
                  <span className="tnum w-11 shrink-0 text-right text-[10px] text-muted">
                    {formatPercent(entry.fraction, 1)}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ) : null}
      </div>

      <div className="flex items-center gap-1 border-t border-line p-2">
        <button
          type="button"
          className="btn"
          onClick={() => void copy(`"${column.name}"`)}
        >
          Copy name
        </button>
        {profile.min !== null && profile.max !== null ? (
          <button
            type="button"
            className="btn"
            onClick={() =>
              onUsePredicate(
                `"${column.name}" BETWEEN '${profile.min}' AND '${profile.max}'`,
              )
            }
          >
            Use range
          </button>
        ) : null}
        <span className="ml-auto text-[10px] text-local" role="status" aria-live="polite">
          {copied}
        </span>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  mono,
  emphasis,
  copyable,
  onCopy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  emphasis?: boolean;
  copyable?: boolean;
  onCopy?: (text: string) => Promise<void>;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <dt className="eyebrow shrink-0">{label}</dt>
      <dd
        className={`min-w-0 truncate text-right text-[11px] ${mono ? 'font-mono' : 'tnum'} ${
          emphasis ? 'text-remote' : 'text-ink'
        }`}
        title={value}
      >
        {copyable && onCopy ? (
          <button type="button" className="hover:text-local" onClick={() => void onCopy(value)}>
            {value}
          </button>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}
